type PreloadOptions = {
  concurrency?: number;
};

const loaded = new Set<string>();
const failed = new Set<string>();
const inFlight = new Map<string, Promise<void>>();

export const toImageSrc = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined;
  const normalized = url.trim();
  return normalized.length > 0 ? normalized : undefined;
};

export const preloadImage = (url: string): Promise<void> => {
  const normalized = url.trim();
  if (normalized.length === 0) return Promise.resolve();
  if (loaded.has(normalized) || failed.has(normalized)) return Promise.resolve();

  const existing = inFlight.get(normalized);
  if (existing) return existing;

  if (typeof Image === 'undefined') {
    loaded.add(normalized);
    return Promise.resolve();
  }

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.decoding = 'async';

    const finalize = (status: 'loaded' | 'failed') => {
      img.onload = null;
      img.onerror = null;
      inFlight.delete(normalized);
      if (status === 'loaded') loaded.add(normalized);
      else failed.add(normalized);
      resolve();
    };

    img.onload = () => finalize('loaded');
    img.onerror = () => finalize('failed');
    img.src = normalized;
  });

  inFlight.set(normalized, promise);
  return promise;
};

export const preloadImages = async (
  urls: Array<string | null | undefined>,
  options?: PreloadOptions
): Promise<void> => {
  const normalized = urls
    .map((url) => (url ? url.trim() : ''))
    .filter((url) => url.length > 0);

  if (normalized.length === 0) return;

  const unique = Array.from(new Set(normalized));
  const concurrency = Math.max(1, Math.min(options?.concurrency ?? 6, unique.length));

  let index = 0;
  const workers = Array.from({ length: concurrency }, async () => {
    while (true) {
      const currentIndex = index;
      index += 1;
      const url = unique[currentIndex];
      if (!url) return;
      await preloadImage(url);
    }
  });

  await Promise.all(workers);
};

